import { Component, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { HttpService } from 'src/app/services/http.service';
import { environment } from 'src/environments/environment';
import {
  confirmAvailable,
  followUpAvailable,
  hasWhatsAppSender,
  isTerminalVerification,
  manualReviewAvailable,
  nonTerminalOutcomesAvailable,
} from './managed-case-action-policy';
import { commissionStatusLabel, managedCaseStatusLabel, verificationOutcomeLabel } from './tiny-promo-labels';

interface OrderContext {
  requestId?: string;
  hubOrderNumber?: number;
  activityName?: string;
  hubName?: string;
  hubId?: string;
  contentType?: string;
  customerName?: string;
  clientId?: string;
  orderStatus?: string;
  createdOn?: string;
  updatedOn?: string;
  totalAmount?: number;
  paidAmount?: number;
  currency?: string;
  promoCode?: string;
  promotionId?: string;
}

interface CommissionLink {
  commissionId?: string;
  transactionRequestId?: string;
  status?: string;
  program?: string;
  promoCode?: string;
  promoterCommissionRate?: number;
  promoterCommissionAmount?: number;
  tinyPromoFeeRate?: number;
  tinyPromoFeeAmount?: number;
  customerPaidAmount?: number;
  currency?: string;
}

interface HistoryEntry {
  id?: string;
  type?: string;
  createdAt?: string;
  newStatus?: string;
  reason?: string;
  actorType?: string;
}

interface ManagedCaseDetail {
  id: string;
  requestId: string;
  hubId: string;
  promoCode: string;
  promoterEmail?: string;
  program: string;
  status: string;
  verificationOutcome?: string;
  whatsAppConnectionState?: string;
  whatsAppSenderNumber?: string;
  whatsAppMessageId?: string;
  whatsAppConnectedAt?: string;
  senderMismatch?: boolean;
  senderMismatchAfterVerification?: boolean;
  senderValidationPending?: boolean;
  followUpAllowed?: boolean;
  lastFollowUpAt?: string;
  nextFollowUpAllowedAt?: string;
  commissionId?: string;
  promoTransactionRequestId?: string;
  manualReviewReason?: string;
  contentType?: string;
  promotionId?: string;
  clientId?: string;
  createdAt?: string;
  updatedAt?: string;
  order?: OrderContext;
  commission?: CommissionLink;
  history?: HistoryEntry[];
}

@Component({
  selector: 'app-tiny-promo-case-detail',
  templateUrl: './tiny-promo-case-detail.component.html',
  styleUrls: ['./tiny-promo-cases.component.scss'],
})
export class TinyPromoCaseDetailComponent implements OnInit, OnDestroy {
  private readonly apiUrl = environment.apiUrl;
  detail: ManagedCaseDetail | null = null;
  loadError = false;
  actionError = false;
  actionMessage = '';
  private routeSub?: Subscription;

  statusLabel = managedCaseStatusLabel;
  outcomeLabel = verificationOutcomeLabel;
  commissionLabel = commissionStatusLabel;

  constructor(
    private httpService: HttpService,
    private route: ActivatedRoute,
    private location: Location,
  ) {}

  ngOnInit() {
    this.routeSub = this.route.paramMap.subscribe((params) => {
      void this.load(params.get('caseId') || '');
    });
  }

  ngOnDestroy() {
    this.routeSub?.unsubscribe();
  }

  get terminal(): boolean {
    return isTerminalVerification(this.detail);
  }

  get autoConfirmed(): boolean {
    return this.detail?.verificationOutcome === 'AUTO_CONFIRMED_TIMEOUT';
  }

  get senderConnected(): boolean {
    return hasWhatsAppSender(this.detail);
  }

  get canConfirm(): boolean {
    return confirmAvailable(this.detail);
  }

  get canRecordFollowUpOutcome(): boolean {
    return nonTerminalOutcomesAvailable(this.detail);
  }

  get canReview(): boolean {
    return manualReviewAvailable(this.detail);
  }

  get canFollowUp(): boolean {
    return followUpAvailable(this.detail);
  }

  back() {
    this.location.back();
  }

  async confirm() {
    if (!window.confirm('This records customer confirmation and can verify the linked commission. It does not pay anyone.')) {
      return;
    }
    await this.outcome('CUSTOMER_CONFIRMED', undefined, 'Outcome recorded: Customer confirmed');
  }

  async notYet() {
    await this.outcome('NOT_YET', undefined, 'Outcome recorded: Not yet');
  }

  async problem() {
    const reason = window.prompt('Problem summary');
    if (reason == null) {
      return;
    }
    await this.outcome('PROBLEM_REPORTED', reason, 'Outcome recorded: Problem reported');
  }

  async unreachable() {
    await this.outcome('UNREACHABLE', undefined, 'Outcome recorded: Unreachable');
  }

  async manualReview() {
    const reason = window.prompt('Manual review reason');
    if (!reason?.trim() || !this.detail) {
      return;
    }
    await this.post(
      `${this.apiUrl}/admin-center/tiny-promo-managed-case/v1/${this.detail.id}/manual-review`,
      { reason: reason.trim() },
      'Outcome recorded: Manual review',
    );
  }

  async followUp(event?: Event) {
    if (!this.canFollowUp || !this.detail) {
      event?.preventDefault();
      event?.stopImmediatePropagation();
      return;
    }
    await this.post(
      `${this.apiUrl}/admin-center/tiny-promo-managed-case/v1/${this.detail.id}/follow-up`,
      {},
      'Follow-up sent successfully',
    );
  }

  guardFollowUpKey(event: KeyboardEvent) {
    if (!this.canFollowUp && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  private async outcome(outcome: string, reason: string | undefined, feedback: string) {
    if (!this.detail) {
      return;
    }
    await this.post(
      `${this.apiUrl}/admin-center/tiny-promo-managed-case/v1/${this.detail.id}/outcome`,
      { outcome, reason },
      feedback,
    );
  }

  private async post(url: string, body: unknown, feedback: string) {
    this.actionError = false;
    this.actionMessage = '';
    try {
      this.detail = await this.httpService.doPost(url, body).toPromise();
      this.actionMessage = feedback;
    } catch (error) {
      this.actionError = true;
      if (this.errorCode(error) === 'FOLLOW_UP_COOLDOWN') {
        this.actionMessage = 'A follow-up was already sent recently.';
        if (this.detail) {
          this.detail = { ...this.detail, followUpAllowed: false };
        }
      } else {
        this.actionMessage = 'The action could not be completed.';
      }
      console.error('Managed case action failed: ', error);
    }
  }

  private async load(caseId: string) {
    this.actionMessage = '';
    this.actionError = false;
    try {
      const url = `${this.apiUrl}/admin-center/tiny-promo-managed-case/v1/${caseId}`;
      this.detail = await this.httpService.doGet(url).toPromise();
      this.loadError = false;
    } catch (error) {
      this.detail = null;
      this.loadError = true;
      console.error('Error getting managed case: ', error);
    }
  }

  private errorCode(error: unknown): string {
    const body = (error as { error?: { errorCode?: string } })?.error;
    return body?.errorCode || '';
  }
}
